import { z } from 'zod';
import { yesNoValues } from 'src/apis/storage/mappings';
import type { StorageLocationWriteData } from 'src/apis/storage/types';
import { networkPointOptionSchema } from 'src/components/form/select/network-point-select/network-point-multi-select';
import { searchableSingleSelectSchema } from 'src/components/form/select/searchable-single-select';
import {
  preprocessAsArray,
  requiredOutput,
} from 'src/components/form/zod-utilities';
import { urlRegex } from 'src/utils/validation';

const marketAreaEntrySchema = z.object({
  id: z.string(), // just used for the form itself
  marketAreaId: searchableSingleSelectSchema,
  injectionPoints: z.preprocess(
    preprocessAsArray,
    z.array(networkPointOptionSchema).default([])
  ),
  withdrawalPoints: z.preprocess(
    preprocessAsArray,
    z.array(networkPointOptionSchema).default([])
  ),
});

export type MarketAreaEntry = z.input<typeof marketAreaEntrySchema>;

export type CreateStorageLocationFormValues = {
  locationName: string | null;
  locationEic: string | null;
  marketAreas: z.input<typeof marketAreaEntrySchema>[];
  virtualPoint: boolean | null;
  comment: string | null;
  link: string | null;
};

const eicRegExp = /^[A-Z0-9]{2}[V-Z][A-Z-0-9]{13}$/;
const invalidEicMessage =
  'The EIC must be exactly 16 characters and match the EIC format (e.g. 21Z000000000004A).';

const createStorageLocationSchema = z.object({
  locationName: z
    .string()
    .max(255, 'Please enter a {label} that is shorter than 255 characters.')
    .nullable()
    .transform(requiredOutput()),
  locationEic: z
    .string()
    .regex(eicRegExp, invalidEicMessage)
    .nullable()
    .transform(requiredOutput()),
  marketAreas: z
    .array(marketAreaEntrySchema)
    .min(1, 'Please add at least one Market Area.'),
  virtualPoint: z
    .enum(yesNoValues)
    .default('false')
    .transform((v) => v === 'true'),
  comment: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v?.trim() ? v : null)),
  link: z
    .string()
    .regex(
      urlRegex,
      'URL format is incorrect. Please enter a valid URL which starts with https://'
    )
    .nullable(),
});

type ValidCreateStorageLocationFormValues = z.output<
  typeof createStorageLocationSchema
>;

export function getCreateStorageLocationSchema() {
  return createStorageLocationSchema;
}

export function formValuesToCreateRequest(
  values: ValidCreateStorageLocationFormValues
): StorageLocationWriteData {
  return {
    locationName: values.locationName,
    locationEic: values.locationEic,
    comment: values.comment,
    link: values.link,
    marketAreas: values.marketAreas.map((item) => ({
      marketAreaId: item.marketAreaId.value,
      injectionPoints: item.injectionPoints.map((np) => np.value),
      withdrawalPoints: item.withdrawalPoints.map((np) => np.value),
    })),
    virtualPoint: values.virtualPoint,
  };
}
